import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useLang, tr } from "../contexts/LangContext";
import api, { formatApiErrorDetail } from "../lib/api";
import { Plus, X, Sailboat, GraduationCap, Calendar, Trash2, ShoppingBag, Download, User as UserIcon, FileText, KeyRound, Check, Anchor, Warehouse, Bell, Shield, Wallet, ClipboardList, Crown, Settings, ArrowRight, ExternalLink } from "lucide-react";

const TABS = ["reservations", "orders", "stages", "myboats", "mygarages", "notifications", "downloads", "profile"];
const TAB_ICON = {
  reservations: Calendar, orders: ShoppingBag, stages: GraduationCap, myboats: Anchor, mygarages: Warehouse, notifications: Bell, downloads: Download, profile: UserIcon,
};

export default function Dashboard() {
  const { user, refresh } = useAuth();
  const { t, lang } = useLang();
  const [tab, setTab] = useState(() => {
    if (typeof window !== "undefined") {
      const params = new URLSearchParams(window.location.search);
      const t0 = params.get("tab");
      if (t0 && TABS.includes(t0)) return t0;
    }
    return "reservations";
  });

  const TAB_LABEL = {
    reservations: t.dashboard.myReservations,
    orders: t.dashboard.myOrders,
    stages: t.dashboard.myStages,
    myboats: tr("Mes bateaux"),
    mygarages: tr("Mes garages"),
    notifications: tr("Notifications"),
    downloads: t.dashboard.myDownloads,
    profile: t.dashboard.profileTab,
  };

  if (!user || user === false) return null;

  return (
    <div className="pt-24 md:pt-28 pb-16 md:pb-24 min-h-screen bg-sand" data-testid="dashboard-page">
      <div className="max-w-7xl mx-auto px-4 md:px-10 lg:px-12">
        <div className="label-eyebrow mb-3">{t.dashboard.welcome}</div>
        <h1 className="font-serif text-4xl lg:text-5xl font-light text-navy">{user.name}</h1>
        <div className="divider-gold mt-5" />
        <p className="text-slate-500 text-sm mt-4">{user.email} · {user.role}</p>

        <StaffSpaces role={user.role} roles={user.roles} />

        <UsefulLinksSection lang={lang} />

        {/* Tabs */}
        <div className="flex flex-nowrap gap-1 border-b border-slate-200 mt-10 mb-8 overflow-x-auto -mx-4 px-4 md:mx-0 md:px-0 scrollbar-thin" data-testid="dashboard-tabs">
          {TABS.map((tk) => {
            const Icon = TAB_ICON[tk];
            return (
              <button
                key={tk}
                onClick={() => setTab(tk)}
                className={`px-4 py-3 text-xs uppercase tracking-[0.2em] border-b-2 transition-colors flex items-center gap-2 whitespace-nowrap shrink-0 ${tab === tk ? "border-club-red text-club-red font-semibold" : "border-transparent text-slate-500 hover:text-navy"}`}
                data-testid={`dashboard-tab-${tk}`}
              >
                <Icon className="w-4 h-4" /> {TAB_LABEL[tk]}
              </button>
            );
          })}
        </div>

        {tab === "reservations" && <ReservationsTab t={t} lang={lang} />}
        {tab === "orders" && <OrdersTab t={t} lang={lang} />}
        {tab === "stages" && <StagesTab t={t} lang={lang} />}
        {tab === "myboats" && <MyBoatsTab />}
        {tab === "mygarages" && <MyGaragesTab />}
        {tab === "notifications" && <NotificationsTab lang={lang} />}
        {tab === "downloads" && <DownloadsTab t={t} />}
        {tab === "profile" && <ProfileTab t={t} user={user} refresh={refresh} />}
      </div>
    </div>
  );
}

/* ---------- Staff Spaces banner ---------- */
const BACKEND = process.env.REACT_APP_BACKEND_URL;

function UsefulLinksSection({ lang }) {
  const [links, setLinks] = useState([]);
  const [cats, setCats] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [l, c] = await Promise.all([api.get("/member/useful-links"), api.get("/link-categories")]);
        setLinks(l.data || []);
        setCats(c.data || []);
      } catch { /* section masquée en cas d'erreur */ }
    })();
  }, []);

  if (links.length === 0) return null;
  const L = (obj, base) => obj[`${base}_${lang}`] || obj[`${base}_fr`] || obj[`${base}_en`] || obj[`${base}_nl`] || "";
  const catName = (id) => {
    const c = cats.find((x) => x.id === id);
    return c ? L(c, "name") : "";
  };

  return (
    <div className="mt-8 bg-white border border-slate-200 p-5" data-testid="useful-links-section">
      <div className="flex items-center gap-2 mb-4">
        <ExternalLink className="w-4 h-4 text-club-red" />
        <div className="label-eyebrow text-club-red">{tr("Inscriptions & liens utiles")}</div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {links.map((l) => (
          <div key={l.id} className="border border-slate-200 p-4 flex flex-col gap-2 hover:border-club-red/40 transition-colors" data-testid={`useful-link-${l.id}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="flex items-center gap-2.5 min-w-0">
                {l.logo_url && (
                  <img
                    src={l.logo_url.startsWith("/uploads/") ? `${BACKEND}/api${l.logo_url}` : l.logo_url}
                    alt={L(l, "title")} className="w-9 h-9 object-contain shrink-0"
                  />
                )}
                <div className="font-serif text-lg text-navy leading-tight truncate">{L(l, "title")}</div>
              </div>
              {l.category_id && catName(l.category_id) && (
                <span className="text-[10px] uppercase tracking-widest text-slate-400 shrink-0 pt-1">{catName(l.category_id)}</span>
              )}
            </div>
            {L(l, "description") && <p className="text-sm text-slate-500 leading-snug flex-1">{L(l, "description")}</p>}
            <a
              href={l.url} target="_blank" rel="noopener noreferrer"
              title={tr("Vous quittez le site RYCM")}
              className="inline-flex items-center justify-center gap-2 bg-navy text-white px-4 py-3.5 text-xs uppercase tracking-[0.2em] hover:bg-club-red transition-colors mt-1"
              data-testid={`useful-link-btn-${l.id}`}
            >
              {L(l, "button") || tr("Lien externe")} <ExternalLink className="w-3.5 h-3.5" />
            </a>
          </div>
        ))}
      </div>
    </div>
  );
}

function StaffSpaces({ role, roles }) {
  const allRoles = new Set(roles && roles.length ? roles : [role]);
  const ALL_SPACES = {
    admin:     { to: "/admin",       label: tr("Espace Admin"),        icon: Settings,      color: "bg-red-600 hover:bg-red-700" },
    treasurer: { to: "/treasury",    label: tr("Trésorerie"),          icon: Wallet,        color: "bg-amber-600 hover:bg-amber-700" },
    secretary: { to: "/secretariat", label: tr("Secrétariat"),         icon: ClipboardList, color: "bg-emerald-600 hover:bg-emerald-700" },
    president: { to: "/president",   label: tr("Espace Président"),    icon: Crown,         color: "bg-fuchsia-600 hover:bg-fuchsia-700" },
    manager:   { to: "/gestion",     label: tr("Espace Gestion"),      icon: Anchor,        color: "bg-cyan-600 hover:bg-cyan-700" },
  };
  // Admin always sees all spaces
  let visibleKeys;
  if (allRoles.has("admin")) {
    visibleKeys = ["admin", "treasurer", "secretary", "president", "manager"];
  } else {
    visibleKeys = ["treasurer", "secretary", "president", "manager"].filter((k) => allRoles.has(k));
  }
  if (visibleKeys.length === 0) return null;
  const spaces = visibleKeys.map((k) => ALL_SPACES[k]);

  return (
    <div className="mt-8 bg-white border border-slate-200 p-5" data-testid="staff-spaces">
      <div className="flex items-center gap-2 mb-4">
        <Shield className="w-4 h-4 text-club-red" />
        <div className="label-eyebrow text-club-red">
          {tr("Mes espaces")} {visibleKeys.length > 1 && <span className="text-slate-400 ml-2 normal-case tracking-normal">({visibleKeys.length} {tr("rôles cumulés")})</span>}
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {spaces.map((s) => {
          const Icon = s.icon;
          return (
            <Link
              key={s.to} to={s.to}
              className={`group ${s.color} text-white px-5 py-4 transition-colors flex items-center justify-between`}
              data-testid={`staff-space-${s.to.slice(1)}`}
            >
              <div className="flex items-center gap-3">
                <Icon className="w-5 h-5" strokeWidth={1.5} />
                <div>
                  <div className="text-xs uppercase tracking-[0.2em] opacity-80">{tr("Accéder à")}</div>
                  <div className="font-serif text-lg leading-tight">{s.label}</div>
                </div>
              </div>
              <ArrowRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
            </Link>
          );
        })}
      </div>
    </div>
  );
}

/* ---------- Reservations (existing flow) ---------- */
function ReservationsTab({ t, lang }) {
  const [reservations, setReservations] = useState([]);
  const [courses, setCourses] = useState([]);
  const [boats, setBoats] = useState([]);
  const [showModal, setShowModal] = useState(null);
  const [form, setForm] = useState({ item_id: "", date: "", notes: "" });
  const [toast, setToast] = useState("");

  const load = async () => {
    try {
      const [r, c, b] = await Promise.all([api.get("/reservations"), api.get("/courses"), api.get("/boats")]);
      setReservations(r.data); setCourses(c.data); setBoats(b.data);
    } catch { /* ignore */ }
  };
  useEffect(() => { load(); }, []);

  const openModal = (type) => { setShowModal(type); setForm({ item_id: "", date: "", notes: "" }); };
  const closeModal = () => setShowModal(null);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/reservations", { type: showModal, ...form });
      closeModal();
      setToast(t.dashboard.bookingCreated);
      await load();
      setTimeout(() => setToast(""), 3000);
    } catch (e) {
      setToast(formatApiErrorDetail(e.response?.data?.detail) || "Erreur");
      setTimeout(() => setToast(""), 3000);
    }
  };
  const cancel = async (id) => {
    await api.delete(`/reservations/${id}`);
    setToast(t.dashboard.bookingCancelled);
    load();
    setTimeout(() => setToast(""), 3000);
  };

  const items = showModal === "course" ? courses : boats;
  const today = new Date().toISOString().split("T")[0];

  return (
    <div>
      <div className="flex gap-3 mb-8 flex-wrap">
        <button onClick={() => openModal("course")} className="inline-flex items-center gap-2 bg-navy text-white px-6 py-3 text-xs uppercase tracking-[0.25em] hover:bg-club-red transition-colors" data-testid="new-course-btn">
          <GraduationCap className="w-4 h-4" /> {t.dashboard.reserveCourse}
        </button>
        <button onClick={() => openModal("boat")} className="inline-flex items-center gap-2 bg-club-yellow text-navy px-6 py-3 text-xs uppercase tracking-[0.25em] hover:bg-club-yellow-dark transition-colors" data-testid="new-boat-btn">
          <Sailboat className="w-4 h-4" /> {t.dashboard.reserveBoat}
        </button>
      </div>

      {reservations.length === 0 ? (
        <div className="bg-white border border-dashed border-slate-300 p-16 text-center" data-testid="empty-reservations">
          <Calendar className="w-10 h-10 text-slate-300 mx-auto mb-4" strokeWidth={1.25} />
          <p className="text-slate-500">{t.dashboard.noReservations}</p>
        </div>
      ) : (
        <div className="bg-white border border-slate-200 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-navy text-white">
              <tr className="text-left">
                {[t.dashboard.type, t.dashboard.item, t.dashboard.date, t.dashboard.status, t.dashboard.actions].map((h) => (
                  <th key={h} className="px-5 py-4 text-xs uppercase tracking-widest font-medium">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {reservations.map((r) => (
                <tr key={r.id} className="border-t border-slate-200 hover:bg-slate-50" data-testid={`reservation-${r.id}`}>
                  <td className="px-5 py-4 capitalize">{r.type}</td>
                  <td className="px-5 py-4 text-navy font-medium">{r.item_name}</td>
                  <td className="px-5 py-4">{new Date(r.date).toLocaleDateString(lang === "en" ? "en-GB" : lang)}</td>
                  <td className="px-5 py-4"><span className="inline-block px-3 py-1 text-xs uppercase tracking-wider bg-club-yellow/20 text-club-yellow-dark">{t.dashboard[r.status] || r.status}</span></td>
                  <td className="px-5 py-4 text-right">
                    <button onClick={() => cancel(r.id)} className="inline-flex items-center gap-1 text-xs uppercase tracking-wider text-slate-500 hover:text-red-600" data-testid={`cancel-${r.id}`}>
                      <Trash2 className="w-3.5 h-3.5" /> {t.dashboard.cancel}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 bg-navy/60 backdrop-blur-sm z-[60] flex items-center justify-center p-6" data-testid="reservation-modal">
          <form onSubmit={submit} className="bg-white max-w-lg w-full p-10 relative">
            <button type="button" onClick={closeModal} className="absolute top-4 right-4 text-slate-500 hover:text-navy" data-testid="modal-close"><X className="w-5 h-5" /></button>
            <div className="label-eyebrow mb-3">{t.dashboard.newReservation}</div>
            <h2 className="font-serif text-3xl text-navy mb-2">{showModal === "course" ? t.dashboard.reserveCourse : t.dashboard.reserveBoat}</h2>
            <div className="divider-gold mb-6" />
            <div className="space-y-4">
              <div>
                <label className="text-xs uppercase tracking-widest text-slate-500 block mb-2">{showModal === "course" ? t.nav.voile : t.nav.fleet || "Item"}</label>
                <select required value={form.item_id} onChange={(e) => setForm({ ...form, item_id: e.target.value })} className="w-full border border-slate-300 px-3 py-3" data-testid="modal-item-select">
                  <option value="">{t.dashboard.selectItem}</option>
                  {items.map((it) => <option key={it.id} value={it.id}>{showModal === "course" ? it[`title_${lang}`] : it.name}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-slate-500 block mb-2">{t.dashboard.date}</label>
                <input required type="date" min={today} value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="w-full border border-slate-300 px-3 py-3" data-testid="modal-date-input" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-slate-500 block mb-2">{t.dashboard.notes}</label>
                <textarea rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="w-full border border-slate-300 px-3 py-3 resize-none" data-testid="modal-notes-input" />
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button type="submit" className="flex-1 bg-navy text-white py-3 text-xs uppercase tracking-[0.25em] hover:bg-club-red" data-testid="modal-confirm">{t.dashboard.confirm}</button>
              <button type="button" onClick={closeModal} className="px-6 border border-slate-300 text-slate-600 hover:border-navy hover:text-navy text-xs uppercase tracking-[0.25em]" data-testid="modal-cancel">{t.dashboard.cancelAction}</button>
            </div>
          </form>
        </div>
      )}

      {toast && (
        <div className="fixed bottom-8 right-8 bg-navy text-white px-6 py-4 shadow-2xl z-[70] flex items-center gap-3" data-testid="toast">
          <Plus className="w-4 h-4 text-club-yellow" /><span className="text-sm">{toast}</span>
        </div>
      )}
    </div>
  );
}

/* ---------- Orders ---------- */
function OrdersTab({ t, lang }) {
  const [orders, setOrders] = useState([]);
  useEffect(() => { api.get("/me/orders").then((r) => setOrders(r.data)).catch(() => {}); }, []);

  if (orders.length === 0) {
    return <Empty t={t} testid="empty-orders" icon={ShoppingBag} label={t.dashboard.noOrders} />;
  }
  return (
    <div className="space-y-3" data-testid="orders-list">
      {orders.map((o) => (
        <div key={o.id} className="bg-white border border-slate-200 p-5" data-testid={`order-${o.id}`}>
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <div className="font-mono text-xs text-slate-400">{o.id.substring(0, 8)}…</div>
              <div className="text-xs text-slate-500 mt-1">{new Date(o.created_at).toLocaleDateString(lang === "en" ? "en-GB" : lang)}</div>
            </div>
            <div className="font-serif text-2xl text-navy">€ {o.total.toFixed(2)}</div>
            <span className={`text-[10px] uppercase tracking-widest px-3 py-1 ${o.status === "paid" ? "bg-green-100 text-green-800" : "bg-club-yellow/20 text-club-yellow-dark"}`}>
              {o.status}
            </span>
          </div>
          <ul className="mt-3 text-sm text-slate-600">
            {o.items.map((it, i) => (
              <li key={i} className="flex justify-between border-t border-slate-100 py-1.5">
                <span>{it.name} <span className="text-slate-400">× {it.quantity}</span></span>
                <span>€ {(it.unit_price * it.quantity).toFixed(2)}</span>
              </li>
            ))}
          </ul>
          {o.discount > 0 && (
            <div className="flex justify-between text-xs text-club-red mt-2 pt-2 border-t border-dashed border-slate-200" data-testid={`order-discount-${o.id}`}>
              <span>Code promo: {o.promo_code}</span>
              <span>− € {Number(o.discount).toFixed(2)}</span>
            </div>
          )}
          <div className="mt-4">
            <a
              href={`${process.env.REACT_APP_BACKEND_URL}/api/me/orders/${o.id}/invoice.pdf`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-navy hover:text-club-red border border-navy hover:border-club-red px-3 py-2"
              data-testid={`invoice-${o.id}`}
            >
              <FileText className="w-3.5 h-3.5" /> Facture PDF
            </a>
          </div>
        </div>
      ))}
    </div>
  );
}

/* ---------- Stages ---------- */
function StagesTab({ t, lang }) {
  const [regs, setRegs] = useState([]);
  useEffect(() => { api.get("/me/stage-registrations").then((r) => setRegs(r.data)).catch(() => {}); }, []);
  if (regs.length === 0) {
    return <Empty t={t} testid="empty-stages" icon={GraduationCap} label={t.dashboard.noStages} />;
  }
  return (
    <div className="bg-white border border-slate-200 overflow-x-auto" data-testid="stages-list">
      <table className="w-full text-sm">
        <thead className="bg-navy text-white text-left">
          <tr>
            <th className="px-4 py-3 text-xs uppercase tracking-widest font-medium">{t.dashboard.stage}</th>
            <th className="px-4 py-3 text-xs uppercase tracking-widest font-medium">{t.stages.participantName}</th>
            <th className="px-4 py-3 text-xs uppercase tracking-widest font-medium">{t.dashboard.status}</th>
            <th className="px-4 py-3 text-xs uppercase tracking-widest font-medium">{t.dashboard.paid}</th>
            <th className="px-4 py-3 text-xs uppercase tracking-widest font-medium">{t.dashboard.date}</th>
          </tr>
        </thead>
        <tbody>
          {regs.map((r) => (
            <tr key={r.id} className="border-t border-slate-100" data-testid={`stage-reg-${r.id}`}>
              <td className="px-4 py-3 text-navy">{r.stage_title}</td>
              <td className="px-4 py-3">{r.participant_name}</td>
              <td className="px-4 py-3"><span className="text-[10px] uppercase tracking-widest bg-slate-100 px-2 py-0.5">{r.status}</span></td>
              <td className="px-4 py-3">{r.paid ? "✅" : "—"}</td>
              <td className="px-4 py-3 text-slate-500">{new Date(r.created_at).toLocaleDateString(lang === "en" ? "en-GB" : lang)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ---------- Downloads ---------- */
function DownloadsTab({ t }) {
  const [pdfs, setPdfs] = useState([]);
  useEffect(() => { api.get("/me/downloads").then((r) => setPdfs(r.data)).catch(() => {}); }, []);
  if (pdfs.length === 0) {
    return <Empty t={t} testid="empty-downloads" icon={FileText} label={t.dashboard.noDownloads} />;
  }
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="downloads-list">
      {pdfs.map((p) => (
        <a key={p.id} href={`${process.env.REACT_APP_BACKEND_URL}/api${p.url}`} target="_blank" rel="noopener noreferrer"
          className="flex items-center gap-3 bg-white p-5 border border-slate-200 hover:border-club-red group" data-testid={`download-${p.id}`}>
          <FileText className="w-6 h-6 text-club-red shrink-0" />
          <div>
            <div className="text-sm text-navy group-hover:text-club-red">{p.title}</div>
            <div className="text-[10px] uppercase tracking-widest text-slate-400">{p.category}</div>
          </div>
        </a>
      ))}
    </div>
  );
}

/* ---------- Profile ---------- */
function ProfileTab({ t, user, refresh }) {
  const [form, setForm] = useState({ name: user.name || "", phone: user.phone || "", address: user.address || "" });
  const [pwForm, setPwForm] = useState({ current_password: "", new_password: "" });
  const [msg, setMsg] = useState("");
  const [pwMsg, setPwMsg] = useState("");
  const [err, setErr] = useState("");

  const saveProfile = async (e) => {
    e.preventDefault(); setErr(""); setMsg("");
    try {
      await api.patch("/me", form);
      setMsg(t.dashboard.profileSaved); refresh();
      setTimeout(() => setMsg(""), 3000);
    } catch (e) { setErr(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const savePw = async (e) => {
    e.preventDefault(); setErr(""); setPwMsg("");
    try {
      await api.post("/me/change-password", pwForm);
      setPwForm({ current_password: "", new_password: "" });
      setPwMsg(t.dashboard.passwordChanged);
      setTimeout(() => setPwMsg(""), 3000);
    } catch (e) { setErr(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl">
      <form onSubmit={saveProfile} className="bg-white border border-slate-200 p-6" data-testid="profile-form">
        <div className="label-eyebrow mb-4 text-club-red">{t.dashboard.profileInfo}</div>
        <h3 className="font-serif text-2xl text-navy mb-1">{user.email}</h3>
        <div className="divider-gold mb-5" />
        <Field label={t.auth.name} value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="profile-name" />
        <Field label={t.dashboard.phone} value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testid="profile-phone" />
        <Field label={t.dashboard.address} value={form.address} onChange={(v) => setForm({ ...form, address: v })} testid="profile-address" />
        {msg && <div className="flex items-center gap-2 text-sm text-green-700 mt-3" data-testid="profile-saved"><Check className="w-4 h-4" />{msg}</div>}
        <button type="submit" className="mt-5 w-full bg-navy text-white py-3 text-xs uppercase tracking-[0.25em] hover:bg-club-red" data-testid="profile-submit">{t.dashboard.saveProfile}</button>
      </form>

      <form onSubmit={savePw} className="bg-white border border-slate-200 p-6" data-testid="password-form">
        <div className="label-eyebrow mb-4 text-club-red">{t.dashboard.changePassword}</div>
        <h3 className="font-serif text-2xl text-navy mb-1"><KeyRound className="w-6 h-6 inline text-club-yellow mr-2" />{t.dashboard.changePassword}</h3>
        <div className="divider-gold mb-5" />
        <Field label={t.dashboard.currentPassword} type="password" value={pwForm.current_password} onChange={(v) => setPwForm({ ...pwForm, current_password: v })} required testid="profile-current-password" />
        <Field label={t.auth.newPassword} type="password" value={pwForm.new_password} onChange={(v) => setPwForm({ ...pwForm, new_password: v })} required testid="profile-new-password" />
        {pwMsg && <div className="flex items-center gap-2 text-sm text-green-700 mt-3" data-testid="password-changed"><Check className="w-4 h-4" />{pwMsg}</div>}
        {err && <div className="text-sm text-red-600 mt-3" data-testid="profile-error">{err}</div>}
        <button type="submit" className="mt-5 w-full bg-navy text-white py-3 text-xs uppercase tracking-[0.25em] hover:bg-club-red" data-testid="profile-password-submit">{t.dashboard.updatePassword}</button>
      </form>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", required, testid }) {
  return (
    <div className="mb-4">
      <label className="text-xs uppercase tracking-widest text-slate-500 mb-1 block">{label}</label>
      <input type={type} required={required} value={value} onChange={(e) => onChange(e.target.value)}
        className="w-full border border-slate-300 outline-none focus:border-navy px-3 py-2.5 bg-white" data-testid={testid} />
    </div>
  );
}

function Empty({ icon: Icon, label, testid }) {
  return (
    <div className="bg-white border border-dashed border-slate-300 p-16 text-center" data-testid={testid}>
      <Icon className="w-10 h-10 text-slate-300 mx-auto mb-4" strokeWidth={1.25} />
      <p className="text-slate-500">{label}</p>
    </div>
  );
}

/* ---------- MY BOATS ---------- */
function MyBoatsTab() {
  const [boats, setBoats] = useState([]);
  const [show, setShow] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", length: "", boat_type: "sail", sail_number: "", notes: "" });
  const [err, setErr] = useState("");

  const load = async () => {
    try { const { data } = await api.get("/me/boats"); setBoats(data); } catch { /* ignore */ }
  };
  useEffect(() => { load(); }, []);

  const reset = () => {
    setForm({ name: "", length: "", boat_type: "sail", sail_number: "", notes: "" });
    setEditing(null); setShow(false); setErr("");
  };

  const submit = async (e) => {
    e.preventDefault(); setErr("");
    const payload = { ...form, length: form.length ? parseFloat(form.length) : null };
    try {
      if (editing) await api.patch(`/me/boats/${editing.id}`, payload);
      else await api.post("/me/boats", payload);
      reset(); load();
    } catch (e) { setErr(e.response?.data?.detail || "Erreur"); }
  };

  const startEdit = (b) => {
    setEditing(b);
    setForm({ name: b.name, length: b.length || "", boat_type: b.boat_type, sail_number: b.sail_number || "", notes: b.notes || "" });
    setShow(true);
  };

  const del = async (id) => {
    if (!window.confirm(tr("Supprimer ce bateau ?"))) return;
    await api.delete(`/me/boats/${id}`); load();
  };

  return (
    <div data-testid="myboats-tab">
      <div className="flex justify-between items-center mb-4">
        <h2 className="font-serif text-2xl text-navy">{tr("Mes bateaux")}</h2>
        <button onClick={() => { setEditing(null); setShow(!show); }} className="inline-flex items-center gap-2 bg-navy text-white px-5 py-2.5 text-xs uppercase tracking-[0.2em] hover:bg-club-red" data-testid="myboat-add">
          <Plus className="w-4 h-4" />{tr("Ajouter")}</button>
      </div>

      {show && (
        <form onSubmit={submit} className="bg-white border border-slate-200 p-6 mb-6 grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="myboat-form">
          <input required placeholder={tr("Nom du bateau")} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="border border-slate-300 px-3 py-2" data-testid="myboat-name" />
          <input type="number" step="0.01" placeholder="Longueur (m)" value={form.length} onChange={(e) => setForm({ ...form, length: e.target.value })} className="border border-slate-300 px-3 py-2" data-testid="myboat-length" />
          <select value={form.boat_type} onChange={(e) => setForm({ ...form, boat_type: e.target.value })} className="border border-slate-300 px-3 py-2" data-testid="myboat-type">
            <option value="sail">{tr("Voile")}</option>
            <option value="motor">{tr("Moteur")}</option>
          </select>
          <input placeholder={tr("N° de voile / immatriculation")} value={form.sail_number} onChange={(e) => setForm({ ...form, sail_number: e.target.value })} className="border border-slate-300 px-3 py-2" data-testid="myboat-sail-number" />
          <input placeholder="Notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="border border-slate-300 px-3 py-2 md:col-span-2" data-testid="myboat-notes" />
          {err && <div className="md:col-span-2 text-sm text-red-600">{err}</div>}
          <div className="md:col-span-2 flex gap-3">
            <button type="submit" className="flex-1 bg-club-red text-white py-3 text-xs uppercase tracking-[0.2em] hover:bg-club-red-dark" data-testid="myboat-submit">
              {editing ? tr("Mettre à jour") : tr("Enregistrer")}
            </button>
            <button type="button" onClick={reset} className="px-5 bg-slate-200 text-navy py-3 text-xs uppercase tracking-[0.2em]" data-testid="myboat-cancel">{tr("Annuler")}</button>
          </div>
        </form>
      )}

      {boats.length === 0 ? (
        <div className="bg-white border border-dashed border-slate-300 p-12 text-center text-slate-500" data-testid="myboats-empty">{tr("Aucun bateau enregistré.")}</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {boats.map((b) => (
            <div key={b.id} className="bg-white border border-slate-200 p-5" data-testid={`myboat-${b.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <Anchor className="w-6 h-6 text-club-red mb-2" strokeWidth={1.5} />
                  <div className="font-serif text-xl text-navy">{b.name}</div>
                  <div className="text-xs uppercase tracking-widest text-club-yellow-dark mt-1">
                    {b.boat_type === "motor" ? "Moteur" : "Voile"} {b.length ? `· ${b.length} m` : ""}
                  </div>
                  {b.sail_number && <div className="text-sm text-slate-600 mt-2 font-mono">{b.sail_number}</div>}
                  {b.notes && <div className="text-xs text-slate-500 mt-2 italic">{b.notes}</div>}
                </div>
                <div className="flex flex-col gap-1">
                  <button onClick={() => startEdit(b)} className="text-[10px] uppercase tracking-widest text-navy hover:underline" data-testid={`myboat-edit-${b.id}`}>{tr("Modifier")}</button>
                  <button onClick={() => del(b.id)} className="text-[10px] uppercase tracking-widest text-red-600 hover:underline" data-testid={`myboat-del-${b.id}`}>Suppr.</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ---------- MY GARAGES ---------- */
function MyGaragesTab() {
  const [garages, setGarages] = useState([]);
  useEffect(() => { api.get("/me/garages").then((r) => setGarages(r.data)).catch(() => {}); }, []);
  return (
    <div data-testid="mygarages-tab">
      <h2 className="font-serif text-2xl text-navy mb-3">{tr("Mes garages")}</h2>
      <p className="text-sm text-slate-500 mb-6">Les emplacements de garage sont assignés par la trésorerie. Contactez le club pour toute demande de modification.</p>
      {garages.length === 0 ? (
        <div className="bg-white border border-dashed border-slate-300 p-12 text-center text-slate-500" data-testid="mygarages-empty">{tr("Aucun garage assigné.")}</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {garages.map((g) => (
            <div key={g.id} className="bg-white border border-slate-200 p-5" data-testid={`mygarage-${g.id}`}>
              <Warehouse className="w-6 h-6 text-club-red mb-2" strokeWidth={1.5} />
              <div className="font-serif text-xl text-navy">{g.name || g.label || `Emplacement ${g.number || ""}`}</div>
              <div className="text-xs uppercase tracking-widest text-club-yellow-dark mt-1">{g.zone || g.type || "—"}</div>
              {g.boat_name && <div className="text-sm text-slate-600 mt-2">Bateau&nbsp;: <span className="text-navy font-medium">{g.boat_name}</span></div>}
              {g.fee_amount && <div className="text-sm text-slate-600 mt-1">Tarif&nbsp;: € {Number(g.fee_amount).toFixed(2)} / an</div>}
              {g.notes && <div className="text-xs text-slate-500 mt-2 italic">{g.notes}</div>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ---------- NOTIFICATIONS ---------- */
function NotificationsTab({ lang }) {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");

  const load = async () => {
    try { const { data } = await api.get("/me/notifications/list"); setItems(data); } catch { /* ignore */ }
  };
  useEffect(() => { load(); }, []);

  const markRead = async (rid) => {
    try { await api.post(`/me/notifications/${rid}/read`); load(); } catch { /* ignore */ }
  };

  const markAll = async () => {
    try { await api.post("/me/notifications/mark-all-read"); load(); } catch { /* ignore */ }
  };

  const filtered = filter === "unread" ? items.filter((i) => !i.read_at) : items;
  const unreadCount = items.filter((i) => !i.read_at).length;

  const TYPE_COLOR = {
    notification: "bg-navy text-white",
    annonce: "bg-club-yellow text-navy",
    convocation: "bg-club-red text-white",
    regate_info: "bg-blue-700 text-white",
    modification: "bg-orange-600 text-white",
    alerte_meteo: "bg-red-700 text-white",
    newsletter: "bg-slate-700 text-white",
  };

  return (
    <div data-testid="notifications-tab">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <h2 className="font-serif text-2xl text-navy">Notifications ({items.length}{unreadCount > 0 && <span className="text-club-red"> · {unreadCount} non lue{unreadCount > 1 ? "s" : ""}</span>})</h2>
        <div className="flex gap-2">
          <button onClick={() => setFilter("all")} className={`px-3 py-2 text-[11px] uppercase tracking-widest ${filter === "all" ? "bg-navy text-white" : "bg-white border border-slate-200 text-slate-600"}`} data-testid="notif-filter-all">{tr("Toutes")}</button>
          <button onClick={() => setFilter("unread")} className={`px-3 py-2 text-[11px] uppercase tracking-widest ${filter === "unread" ? "bg-navy text-white" : "bg-white border border-slate-200 text-slate-600"}`} data-testid="notif-filter-unread">{tr("Non lues")}</button>
          {unreadCount > 0 && (
            <button onClick={markAll} className="px-3 py-2 text-[11px] uppercase tracking-widest bg-club-red text-white hover:bg-club-red-dark" data-testid="notif-mark-all">{tr("Tout marquer lu")}</button>
          )}
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="bg-white border border-dashed border-slate-300 p-12 text-center text-slate-500" data-testid="notif-empty">{tr("Aucune notification.")}</div>
      ) : (
        <div className="space-y-3">
          {filtered.map((n) => (
            <div key={n.read_id} className={`bg-white border p-5 ${!n.read_at ? "border-l-4 border-l-club-red border-slate-200" : "border-slate-200 opacity-80"}`} data-testid={`notif-${n.read_id}`}>
              <div className="flex items-start justify-between gap-3 mb-2">
                <div className="flex items-center gap-2">
                  <span className={`text-[10px] uppercase tracking-widest px-2 py-1 font-semibold ${TYPE_COLOR[n.type] || "bg-slate-200"}`}>
                    {n.type.replace(/_/g, " ")}
                  </span>
                  <span className="text-xs text-slate-500">{new Date(n.created_at).toLocaleString(lang === "en" ? "en-GB" : lang)}</span>
                </div>
                {!n.read_at && (
                  <button onClick={() => markRead(n.read_id)} className="text-[10px] uppercase tracking-widest text-club-red hover:underline" data-testid={`notif-mark-${n.read_id}`}>Marquer lu</button>
                )}
              </div>
              <h3 className="font-serif text-xl text-navy">{n[`title_${lang}`] || n.title_fr}</h3>
              <p className="text-sm text-slate-700 mt-2 whitespace-pre-line">{n[`body_${lang}`] || n.body_fr}</p>
              {n.attachments && n.attachments.length > 0 && (
                <div className="mt-4 flex flex-wrap gap-2">
                  {n.attachments.map((a, i) => (
                    <a
                      key={i}
                      href={`${process.env.REACT_APP_BACKEND_URL}/api${a.url}`}
                      target="_blank" rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 text-xs uppercase tracking-widest text-navy border border-navy px-3 py-1.5 hover:bg-navy hover:text-white"
                      data-testid={`notif-attach-${n.read_id}-${i}`}
                    >
                      <FileText className="w-3.5 h-3.5" /> {a.filename}
                    </a>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
